// Offer readiness: one row per applicable approval (Complete or Missing), led by the package itself while it is not yet
// sent. Extend verbal offer stays aria-disabled, described by the first missing row, until the gate is Ready (his
// rule: block verbal offer until all applicable approvals complete). Seats without the act read it in view mode.
import { ACTION_LABELS, Gate, OFFER_PACKAGE_STATUS, SURFACE_LABELS, type GateRow } from "@/mc";
import type { CompOut } from "./CompensationTab";
import { applicable } from "./CompensationTab";
import { ROLE_WORD } from "./ApprovalsPanel";
import { PKG_RULES, READINESS_LABELS } from "./labels";
import { PACKAGE_STATUS_WORD } from "./PackageCard";

export interface ReadinessGateProps {
  comp: CompOut;
  mode?: "view" | undefined;
  onExtend?: (() => void) | undefined;
}

export function readinessRows(comp: CompOut): GateRow[] {
  const pkg = comp.package ?? null;
  const rows: GateRow[] = [];
  if (!pkg) {
    rows.push({ status: "todo", text: OFFER_PACKAGE_STATUS.draft, sub: READINESS_LABELS.missing });
    return rows;
  }
  if (pkg.status === "draft" || pkg.status === "revision_required" || pkg.status === "rejected") {
    rows.push({ status: pkg.status === "draft" ? "todo" : "bad", text: PACKAGE_STATUS_WORD[pkg.status], sub: READINESS_LABELS.missing });
  }
  const complete = new Set(comp.readiness?.complete ?? []);
  const missing = new Set(comp.readiness?.missing ?? []);
  for (const row of applicable(comp)) {
    const done = complete.has(row.requirement) || (!missing.has(row.requirement) && row.state === "approved");
    rows.push({
      status: done ? "ok" : row.state === "rejected" || row.state === "revision_required" ? "bad" : "todo",
      text: ROLE_WORD[row.requirement],
      sub: done ? READINESS_LABELS.complete : READINESS_LABELS.missing,
    });
  }
  return rows;
}

export function ReadinessGate({ comp, mode, onExtend }: ReadinessGateProps) {
  const rows = readinessRows(comp);
  const ready = comp.readiness?.ready ?? rows.every((row) => row.status === "ok");
  const pkg = comp.package ?? null;
  return (
    <div className="mc-pkg-gate">
      <Gate
        title={SURFACE_LABELS.offerReadiness}
        verdict={pkg ? PACKAGE_STATUS_WORD[pkg.status] : OFFER_PACKAGE_STATUS.draft}
        rows={rows}
        ready={ready}
        mode={mode}
        action={{ label: ACTION_LABELS.extendVerbalOffer, onClick: onExtend, hs: "offer.pkg.extend" }}
      />
      <p className="mc-hint">{PKG_RULES.blockVerbal}</p>
    </div>
  );
}
